import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { UPDATE_CONTACT_INFO_MUTATION } from "@/api/mutations/updateContactInfo";
import {
  UpdateContactInfoMutation,
  UpdateContactInfoMutationVariables,
} from "@/__generated__/graphql";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseUpdateContactInfoParams {
  onCompleted?: (data: UpdateContactInfoMutation) => void;
  onError?: (error: unknown) => void;
}

export interface UpdateContactInfoResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UpdateContactInfoInput {
  userId: string;
  email: string;
  phoneNumber: string;
}

export interface UseUpdateContactInfoReturn {
  loading: boolean;
  error: Error | undefined;
}

export const useUpdateContactInfo = ({
  onCompleted,
  onError,
}: UseUpdateContactInfoParams = {}): [
  (input: UpdateContactInfoInput) => Promise<UpdateContactInfoResult>,
  UseUpdateContactInfoReturn,
] => {
  const [updateContactInfoMutation, { loading, error }] = useMutation<
    UpdateContactInfoMutation,
    UpdateContactInfoMutationVariables
  >(UPDATE_CONTACT_INFO_MUTATION, {
    onCompleted,
    onError,
    refetchQueries: ["MyProfile"],
    awaitRefetchQueries: true,
  });

  const updateContactInfo = useCallback(
    async (input: UpdateContactInfoInput): Promise<UpdateContactInfoResult> => {
      try {
        const result = await updateContactInfoMutation({
          variables: {
            userId: input.userId,
            email: input.email,
            phoneNumber: input.phoneNumber,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );

        return errorHandler.toResult();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "An unknown error occurred";
        return {
          success: false,
          errors: [{ message, title: "Mutation Error" }],
        };
      }
    },
    [updateContactInfoMutation],
  );

  return [updateContactInfo, { loading, error: error as Error | undefined }];
};
